import { QueryParameterBag } from '@smithy/types'

import {
  DroidMapMetadata,
  OffsetPaged,
  ServiceClient,
  ServiceClientContext,
  StandardOrgIds,
  queryParameterBagFromOffsetPaginationControls,
} from '@droidmap/shared-service-contract'

import {
  AcceptedOrganisationInvitation,
  CreateOrganisationInvitationInput,
  ListOrganisationInvitationsInput,
  MyOrganisationInvitation,
  OrganisationInvitation,
  OrganisationInvitationInput,
  OrganisationInvitationList,
  RespondToOrganisationInvitationInput,
  UpdateOrganisationInvitationInput,
} from '../models/organisation-invitation.js'
import {
  ListOrganisationsByMemberInput,
  ListOrganisationsInput,
} from '../models/organisation-list.js'
import {
  AssignOrgCreditPlanInput,
  AssignOrgPlanInput,
  ChangeOrgCreditPlanInput,
  CreateGroupInput,
  CreateGroupUserInput,
  CreateOrganisationInput,
  CreateOrganisationUserInput,
  DeleteGroupInput,
  DeleteGroupUserInput,
  DeleteOrganisationInput,
  DeleteOrganisationUserInput,
  GetOrganisationInput,
  GetOrganisationUserInput,
  GetOrganisationUserMembershipInput,
  Group,
  GroupUser,
  ListGroupUsersByGroupInput,
  ListGroupsByOrganisationInput,
  ListGroupsByOrganisationUserInput,
  ListOrgInvoicesInput,
  ListOrganisationUsersByGroupInput,
  ListOrganisationUsersByOrganisationInput,
  ListOrganisationsByUserInput,
  OrgBillingCardResponse,
  OrgBillingCheckoutUrl,
  OrgBillingInput,
  OrgBillingInvoice,
  OrgBillingState,
  OrgCreditPlanAssignment,
  OrgCreditPlanChange,
  OrgCreditUsageResponse,
  OrgPlanAssignment,
  OrgStorageStatus,
  OrgWalletRecharge,
  Organisation,
  OrganisationUser,
  OrganisationUserMembership,
  ProUpgradeCancellation,
  ProUpgradeResult,
  RestoreOrganisationInput,
  StartProUpgradeInput,
  UpdateGroupInput,
  UpdateOrgWalletRechargeInput,
  UpdateOrganisationInput,
  UpdateOrganisationUserInput,
  WalletTopUpResult,
} from '../models/organisation-models.js'

export class OrganisationServiceClient extends ServiceClient {
  public static readonly SERVICE_NAME = 'OrganisationService'

  constructor(input: ServiceClientContext) {
    super({ ...input, serviceName: OrganisationServiceClient.SERVICE_NAME })
  }

  /* ------------------------------ Organisation ------------------------------ */

  createOrganisation = async (
    createOrganisationInput: CreateOrganisationInput,
    metadata?: DroidMapMetadata,
  ): Promise<Organisation> => {
    const organisation = await this.callApiRouteWithToken<
      CreateOrganisationInput,
      Organisation
    >({
      method: 'POST',
      path: '/organisations',
      body: createOrganisationInput,
      metadata,
    })

    if (organisation === undefined) {
      throw new Error(
        'Organisation was not created with input: ' +
          JSON.stringify(createOrganisationInput),
      )
    }

    return organisation
  }

  getOrganisation = async (
    getOrganisationInput: GetOrganisationInput,
    metadata?: DroidMapMetadata,
  ): Promise<Organisation> => {
    const organisation = await this.callApiRouteWithToken<
      GetOrganisationInput,
      Organisation
    >({
      method: 'GET',
      path: `/organisations/${getOrganisationInput.orgId}`,
      metadata,
    })

    return organisation
  }

  /**
   * The org's storage-allowance position (#1986) — used hot bytes vs the
   * effective upload cap. Called by asset-service on the upload path (with
   * its internal service token, which bypasses the per-user permission
   * check) and usable by the billing/usage UI (VIEW_ORGANIZATION-gated).
   */
  getOrgStorageStatus = async (
    getOrgStorageStatusInput: GetOrganisationInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrgStorageStatus> => {
    return this.callApiRouteWithToken<GetOrganisationInput, OrgStorageStatus>({
      method: 'GET',
      path: `/organisations/${getOrgStorageStatusInput.orgId}/storage-status`,
      metadata,
    })
  }

  updateOrganisation = async (
    updateOrganisationInput: UpdateOrganisationInput,
    metadata?: DroidMapMetadata,
  ): Promise<Organisation> => {
    const organisation = await this.callApiRouteWithToken<
      UpdateOrganisationInput,
      Organisation
    >({
      method: 'PATCH',
      path: `/organisations/${updateOrganisationInput.orgId}`,
      body: updateOrganisationInput,
      metadata,
    })

    return organisation
  }

  deleteOrganisation = async (
    deleteOrganisationInput: DeleteOrganisationInput,
    metadata?: DroidMapMetadata,
  ): Promise<Organisation> => {
    const group = await this.callApiRouteWithToken<
      DeleteOrganisationInput,
      Organisation
    >({
      method: 'DELETE',
      path: `/organisations/${deleteOrganisationInput.orgId}`,
      metadata,
    })

    return group
  }

  /**
   * Bring a `pending_delete` org back inside its retention window (#2973).
   * Returns the restored org — `trial` if its trial window is still open,
   * otherwise `frozen` awaiting a Pro upgrade.
   */
  restoreOrganisation = async (
    restoreOrganisationInput: RestoreOrganisationInput,
    metadata?: DroidMapMetadata,
  ): Promise<Organisation> => {
    return await this.callApiRouteWithToken<
      RestoreOrganisationInput,
      Organisation
    >({
      method: 'POST',
      path: `/organisations/${restoreOrganisationInput.orgId}/restore`,
      metadata,
    })
  }

  listOrganisationsByUser = async (
    input: ListOrganisationsByUserInput,
    metadata?: DroidMapMetadata,
  ): Promise<OffsetPaged<Organisation>> => {
    const { userId, page, pageSize } = input

    const queryParameters: QueryParameterBag =
      queryParameterBagFromOffsetPaginationControls({ page, pageSize })

    return await this.callApiRouteWithToken<
      ListOrganisationsByUserInput,
      OffsetPaged<Organisation>
    >({
      method: 'GET',
      path: `/users/${userId}/organisations`,
      queryParameters,
      metadata,
    })
  }

  /* --------------------------- Internal reads ------------------------------ */

  /**
   * Every organisation on the platform, filtered, sorted and paged. Answers
   * only an internal-pool caller; the org-scoped permission model does not
   * apply, because there is no single org to scope to.
   */
  listOrganisations = async (
    input: ListOrganisationsInput = {},
    metadata?: DroidMapMetadata,
  ): Promise<OffsetPaged<Organisation>> => {
    const { page, pageSize, sortBy, sortDir, filters } = input

    const queryParameters: QueryParameterBag =
      queryParameterBagFromOffsetPaginationControls({
        page,
        pageSize,
        sortBy,
        sortDir,
      })
    if (filters && filters.length > 0) {
      queryParameters['filters'] = JSON.stringify(filters)
    }

    return await this.callApiRouteWithToken<
      undefined,
      OffsetPaged<Organisation>
    >({
      method: 'GET',
      path: '/internal/organisations',
      queryParameters,
      metadata,
    })
  }

  /**
   * One organisation by id with no membership or permission check. Answers
   * only an internal-pool caller.
   */
  getOrganisationInternal = async (
    input: GetOrganisationInput,
    metadata?: DroidMapMetadata,
  ): Promise<Organisation> => {
    return await this.callApiRouteWithToken<undefined, Organisation>({
      method: 'GET',
      path: `/internal/organisations/${input.orgId}`,
      metadata,
    })
  }

  /**
   * The organisations a user holds a membership row in, including inactive
   * memberships. Answers only an internal-pool caller; unlike
   * {@link listOrganisationsByUser} the caller need not be that user.
   */
  listOrganisationsByMember = async (
    input: ListOrganisationsByMemberInput,
    metadata?: DroidMapMetadata,
  ): Promise<OffsetPaged<Organisation>> => {
    const { userId, page, pageSize, activeOnly } = input

    const queryParameters: QueryParameterBag = {
      ...queryParameterBagFromOffsetPaginationControls({ page, pageSize }),
      ...(activeOnly !== undefined ? { activeOnly: String(activeOnly) } : {}),
    }

    return await this.callApiRouteWithToken<
      undefined,
      OffsetPaged<Organisation>
    >({
      method: 'GET',
      path: `/internal/users/${userId}/organisations`,
      queryParameters,
      metadata,
    })
  }

  /**
   * An organisation's membership rows, including inactive ones unless
   * `activeOnly` says otherwise. Answers only an internal-pool caller, and
   * makes no membership check on it.
   */
  listOrganisationUsersInternal = async (
    input: ListOrganisationUsersByOrganisationInput,
    metadata?: DroidMapMetadata,
  ): Promise<OffsetPaged<OrganisationUser>> => {
    const { orgId, page, pageSize, filter, sortBy, sortDir, activeOnly } = input

    const queryParameters: QueryParameterBag = {
      ...queryParameterBagFromOffsetPaginationControls({
        page,
        pageSize,
        filter,
        sortBy,
        sortDir,
      }),
      ...(activeOnly !== undefined ? { activeOnly: String(activeOnly) } : {}),
    }

    return await this.callApiRouteWithToken<
      undefined,
      OffsetPaged<OrganisationUser>
    >({
      method: 'GET',
      path: `/internal/organisations/${orgId}/organisationUsers`,
      queryParameters,
      metadata,
    })
  }

  /**
   * An organisation's groups, with no membership or permission check.
   * Answers only an internal-pool caller.
   */
  listGroupsByOrganisationInternal = async (
    input: ListGroupsByOrganisationInput,
    metadata?: DroidMapMetadata,
  ): Promise<OffsetPaged<Group>> => {
    const { orgId, page, pageSize, filter, sortBy, sortDir } = input

    const queryParameters: QueryParameterBag =
      queryParameterBagFromOffsetPaginationControls({
        page,
        pageSize,
        filter,
        sortBy,
        sortDir,
      })

    return await this.callApiRouteWithToken<undefined, OffsetPaged<Group>>({
      method: 'GET',
      path: `/internal/organisations/${orgId}/groups`,
      queryParameters,
      metadata,
    })
  }

  /**
   * A group's membership rows, with no membership or permission check.
   * Answers only an internal-pool caller.
   */
  listGroupUsersByGroupInternal = async (
    input: ListGroupUsersByGroupInput,
    metadata?: DroidMapMetadata,
  ): Promise<OffsetPaged<GroupUser>> => {
    const { groupId, page, pageSize } = input

    const queryParameters: QueryParameterBag =
      queryParameterBagFromOffsetPaginationControls({ page, pageSize })

    return await this.callApiRouteWithToken<undefined, OffsetPaged<GroupUser>>({
      method: 'GET',
      path: `/internal/groups/${groupId}/groupUsers`,
      queryParameters,
      metadata,
    })
  }

  /* ------------------------------ Org Billing ------------------------------- */

  /**
   * Fetch the org's Lago/Stripe billing state for the billing UI
   * (has-payment-method, Stripe attachment, last invoice/payment status).
   * Org-admin only — the service gates this on `UPDATE_ORGANIZATION`, which
   * only the `org_admin` role holds. Read-only: never mutates Lago.
   */
  getOrgBillingState = async (
    input: OrgBillingInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrgBillingState> => {
    return await this.callApiRouteWithToken<OrgBillingInput, OrgBillingState>({
      method: 'GET',
      path: `/organisations/${input.orgId}/billing`,
      metadata,
    })
  }

  /**
   * Generate a fresh Stripe-hosted Checkout URL for collecting / updating the
   * org's payment method. Org-admin only. The URL is NEVER cached (the Lago
   * `checkout_url` expires after 24h) — call this each time the admin opens
   * the payment-method flow. POST because it has side effects: it lazy-ensures
   * the org's Stripe customer attachment before generating the URL.
   */
  createOrgBillingCheckoutUrl = async (
    input: OrgBillingInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrgBillingCheckoutUrl> => {
    return await this.callApiRouteWithToken<
      OrgBillingInput,
      OrgBillingCheckoutUrl
    >({
      method: 'POST',
      path: `/organisations/${input.orgId}/billing/checkout-url`,
      metadata,
    })
  }

  /**
   * Assign the org to an ADR-0013 catalogue plan tier (#1991/#2022): moves the
   * Lago subscription onto the tier's plan, provisions the tier's wallet, and
   * stamps `plan_tier` / `billing_mode` on the org row. Org-admin only. The
   * public route accepts only *listed* tiers and rejects `grantedCredits`
   * (both are sales/ops-only).
   */
  assignOrgPlan = async (
    input: AssignOrgPlanInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrgPlanAssignment> => {
    return await this.callApiRouteWithToken<
      AssignOrgPlanInput,
      OrgPlanAssignment
    >({
      method: 'PUT',
      path: `/organisations/${input.orgId}/billing/plan`,
      body: input,
      metadata,
    })
  }

  /**
   * Put the org on an ADR-0027 credit plan (#3001): moves its Lago
   * subscription onto the plan, anniversary-billed, and stamps
   * `credit_plan_tier`. Org-admin only, and refused (403) outside dev until the
   * #3005 migration.
   */
  assignOrgCreditPlan = async (
    input: AssignOrgCreditPlanInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrgCreditPlanAssignment> => {
    return await this.callApiRouteWithToken<
      { creditPlanTier: AssignOrgCreditPlanInput['creditPlanTier'] },
      OrgCreditPlanAssignment
    >({
      method: 'PUT',
      path: `/organisations/${input.orgId}/billing/credit-plan`,
      body: { creditPlanTier: input.creditPlanTier },
      metadata,
    })
  }

  /**
   * Start the payment-gated move onto Pro (#2107). Charges the up-front
   * payment ($50) and marks the org pending WITHOUT flipping the tier — the org
   * becomes Pro only once Lago confirms the payment. Returns
   * `{ status: 'pending_payment', paidCredits }`. Org-admin only; 400 when the
   * org is already Pro or an upgrade is already awaiting payment.
   *
   * `afterCheckout` marks the #2196 auto-continue — the call fired when the
   * admin returns from a Stripe Checkout the frontend saw succeed. It is
   * observability only (#2535): the server records it so a refusal that follows
   * a successful Checkout is distinguishable in logs and metrics from one with
   * no Checkout behind it, and never branches the charge decision on it (a
   * client-supplied return is not proof of a card — #2215).
   */
  startProUpgrade = async (
    input: StartProUpgradeInput,
    metadata?: DroidMapMetadata,
  ): Promise<ProUpgradeResult> => {
    return await this.callApiRouteWithToken<
      StartProUpgradeInput,
      ProUpgradeResult
    >({
      method: 'POST',
      path: `/organisations/${input.orgId}/billing/plan/pro-upgrade`,
      // Only sent when true — an omitted param and `false` mean the same thing
      // server-side, and the route predates the param entirely.
      ...(input.afterCheckout
        ? { queryParameters: { afterCheckout: 'true' } }
        : {}),
      metadata,
    })
  }

  /**
   * Abandon a Pro upgrade that is awaiting its up-front payment (#2222) — the
   * self-serve way out of the "awaiting payment" state when the charge stalls
   * (e.g. an abandoned 3-D Secure step-up). Clears the pending flag and voids
   * the outstanding Lago invoice so it can't settle later. Org-admin only.
   *
   * Idempotent: cancelling when nothing is pending answers `not_pending`
   * rather than erroring. Reconciles against Lago first — if the up-front
   * payment actually succeeded it answers `payment_succeeded` and leaves the
   * upgrade to complete rather than cancelling a charge the customer paid.
   */
  cancelProUpgrade = async (
    input: OrgBillingInput,
    metadata?: DroidMapMetadata,
  ): Promise<ProUpgradeCancellation> => {
    return await this.callApiRouteWithToken<
      OrgBillingInput,
      ProUpgradeCancellation
    >({
      method: 'POST',
      path: `/organisations/${input.orgId}/billing/plan/pro-upgrade/cancel`,
      metadata,
    })
  }

  /**
   * List the org's finalized Lago invoices for the billing-history card
   * (#2022) — amount, payment status, issuing date, and the hosted PDF link.
   * Org-admin only. Newest-first (Lago's default ordering).
   */
  listOrgBillingInvoices = async (
    input: ListOrgInvoicesInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrgBillingInvoice[]> => {
    return await this.callApiRouteWithToken<
      ListOrgInvoicesInput,
      OrgBillingInvoice[]
    >({
      method: 'GET',
      path: `/organisations/${input.orgId}/billing/invoices`,
      metadata,
    })
  }

  /**
   * The brand and last digits of the card Lago would charge (#3006). Org-admin
   * only.
   */
  getOrgBillingCard = async (
    input: OrgBillingInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrgBillingCardResponse> => {
    return await this.callApiRouteWithToken<
      OrgBillingInput,
      OrgBillingCardResponse
    >({
      method: 'GET',
      path: `/organisations/${input.orgId}/billing/card`,
      metadata,
    })
  }

  /**
   * Move the org onto a paid credit plan (#3006). An upgrade applies now and a
   * downgrade waits for the period end. Org-admin only.
   */
  changeOrgCreditPlan = async (
    input: ChangeOrgCreditPlanInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrgCreditPlanChange> => {
    return await this.callApiRouteWithToken<
      ChangeOrgCreditPlanInput,
      OrgCreditPlanChange
    >({
      method: 'POST',
      path: `/organisations/${input.orgId}/billing/credit-plan/change`,
      body: input,
      metadata,
    })
  }

  /** Abandon a Trial's conversion while its fee settles (#3006). Org-admin only. */
  cancelPendingOrgCreditPlan = async (
    input: OrgBillingInput,
    metadata?: DroidMapMetadata,
  ): Promise<{ status: 'cancelled' | 'not_pending' | 'payment_succeeded' }> => {
    return await this.callApiRouteWithToken<
      OrgBillingInput,
      { status: 'cancelled' | 'not_pending' | 'payment_succeeded' }
    >({
      method: 'DELETE',
      path: `/organisations/${input.orgId}/billing/credit-plan/pending`,
      metadata,
    })
  }

  /** Cancel a downgrade waiting for the period end (#3006). Org-admin only. */
  cancelScheduledOrgCreditPlanChange = async (
    input: OrgBillingInput,
    metadata?: DroidMapMetadata,
  ): Promise<void> => {
    await this.callApiRouteWithToken<OrgBillingInput, unknown>({
      method: 'DELETE',
      path: `/organisations/${input.orgId}/billing/credit-plan/scheduled`,
      metadata,
    })
  }

  /**
   * Where a credit-plan org's credits went this period (#3006). Org-admin only.
   * `usage` is `null` for an org not on a credit plan.
   */
  getOrgCreditUsage = async (
    input: OrgBillingInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrgCreditUsageResponse> => {
    return await this.callApiRouteWithToken<
      OrgBillingInput,
      OrgCreditUsageResponse
    >({
      method: 'GET',
      path: `/organisations/${input.orgId}/billing/credits`,
      metadata,
    })
  }

  /**
   * Read the org's prepaid-wallet auto-recharge state (#2022) — the advisory
   * ongoing balance plus the configured threshold recharge rule. Org-admin
   * only. `balanceCents`/`rechargeRule` are `null` when the org has no active
   * wallet (postpaid / never provisioned).
   */
  getOrgWalletRecharge = async (
    input: OrgBillingInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrgWalletRecharge> => {
    return await this.callApiRouteWithToken<OrgBillingInput, OrgWalletRecharge>(
      {
        method: 'GET',
        path: `/organisations/${input.orgId}/billing/wallet`,
        metadata,
      },
    )
  }

  /**
   * Charge the org's auto-recharge amount to the card on file now, without
   * waiting for the balance to fall to the threshold (#2961). Org-admin only.
   * Runs the same guards as the hourly auto-recharge; a guard that refuses
   * answers a non-`charged` status rather than an error, so the UI can say
   * why. The credit lands once Lago settles the payment.
   */
  topUpOrgWallet = async (
    input: OrgBillingInput,
    metadata?: DroidMapMetadata,
  ): Promise<WalletTopUpResult> => {
    return await this.callApiRouteWithToken<OrgBillingInput, WalletTopUpResult>(
      {
        method: 'POST',
        path: `/organisations/${input.orgId}/billing/wallet/top-up`,
        metadata,
      },
    )
  }

  /**
   * Set the org's prepaid-wallet threshold auto-recharge rule (#2022).
   * Org-admin only. Errors (404) when the org has no prepaid wallet, and (400)
   * for a rule below the tier floor or for `rechargeRule: null` on Pro, where
   * auto-recharge cannot be turned off (#2992).
   */
  updateOrgWalletRecharge = async (
    input: UpdateOrgWalletRechargeInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrgWalletRecharge> => {
    return await this.callApiRouteWithToken<
      UpdateOrgWalletRechargeInput,
      OrgWalletRecharge
    >({
      method: 'PUT',
      path: `/organisations/${input.orgId}/billing/wallet/recharge`,
      body: input,
      metadata,
    })
  }

  /* ---------------------------- OrganisationUser ---------------------------- */

  createOrganisationUser = async (
    createOrganisationUserInput: CreateOrganisationUserInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrganisationUser> => {
    const organisationUser = await this.callApiRouteWithToken<
      CreateOrganisationUserInput,
      OrganisationUser
    >({
      method: 'POST',
      path: `/organisations/${createOrganisationUserInput.orgId}/organisationUsers`,
      body: createOrganisationUserInput,
      metadata,
    })

    if (organisationUser === undefined) {
      throw new Error(
        'Organisation user was not created with input: ' +
          JSON.stringify(createOrganisationUserInput),
      )
    }

    return organisationUser
  }

  getOrganisationUser = async (
    getOrganisationUserInput: GetOrganisationUserInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrganisationUser> => {
    const orgUser = await this.callApiRouteWithToken<
      GetOrganisationUserInput,
      OrganisationUser
    >({
      method: 'GET',
      path: `/organisations/${getOrganisationUserInput.orgId}/organisationUsers/${getOrganisationUserInput.userId}`,
      metadata,
    })

    return orgUser
  }

  /**
   * Whether a user is a member of an organisation — independent of any role
   * or group they hold. Returns `{ isMember: false }` rather than throwing
   * when there is no membership.
   */
  getOrganisationUserMembership = async (
    input: GetOrganisationUserMembershipInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrganisationUserMembership> => {
    // Nobody is a member of the `system` sentinel. The route 400s it as a
    // malformed org id, and callers turned that into a 502 (#3030).
    if (input.orgId === StandardOrgIds.SYSTEM) {
      return { isMember: false, isActive: false }
    }

    return await this.callApiRouteWithToken<
      GetOrganisationUserMembershipInput,
      OrganisationUserMembership
    >({
      method: 'GET',
      path: `/organisations/${input.orgId}/organisationUsers/${input.userId}/membership`,
      metadata,
    })
  }

  updateOrganisationUser = async (
    updateOrganisationUserInput: UpdateOrganisationUserInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrganisationUser> => {
    const organisationUser = await this.callApiRouteWithToken<
      UpdateOrganisationUserInput,
      OrganisationUser
    >({
      method: 'PATCH',
      path: `/organisations/${updateOrganisationUserInput.orgId}/organisationUsers/${updateOrganisationUserInput.userId}`,
      body: updateOrganisationUserInput,
      metadata,
    })

    return organisationUser
  }

  deleteOrganisationUser = async (
    deleteOrganisationUserInput: DeleteOrganisationUserInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrganisationUser> => {
    const organisationUser = await this.callApiRouteWithToken<
      DeleteOrganisationUserInput,
      OrganisationUser
    >({
      method: 'DELETE',
      path: `/organisations/${deleteOrganisationUserInput.orgId}/organisationUsers/${deleteOrganisationUserInput.userId}`,
      metadata,
    })

    return organisationUser
  }

  listOrganisationUsersByOrganisation = async (
    input: ListOrganisationUsersByOrganisationInput,
    metadata?: DroidMapMetadata,
  ): Promise<OffsetPaged<OrganisationUser>> => {
    const { orgId, page, pageSize, filter, sortBy, sortDir, activeOnly } = input

    const queryParameters: QueryParameterBag = {
      ...queryParameterBagFromOffsetPaginationControls({
        page,
        pageSize,
        filter,
        sortBy,
        sortDir,
      }),
      ...(activeOnly !== undefined ? { activeOnly: String(activeOnly) } : {}),
    }

    return await this.callApiRouteWithToken<
      ListOrganisationUsersByOrganisationInput,
      OffsetPaged<OrganisationUser>
    >({
      method: 'GET',
      path: `/organisations/${orgId}/organisationUsers`,
      queryParameters,
      metadata,
    })
  }

  listOrganisationUsersByGroup = async (
    input: ListOrganisationUsersByGroupInput,
    metadata?: DroidMapMetadata,
  ): Promise<OffsetPaged<OrganisationUser>> => {
    const { groupId, page, pageSize } = input

    const queryParameters: QueryParameterBag =
      queryParameterBagFromOffsetPaginationControls({ page, pageSize })

    return await this.callApiRouteWithToken<
      ListOrganisationUsersByGroupInput,
      OffsetPaged<OrganisationUser>
    >({
      method: 'GET',
      path: `/groups/${groupId}/organisationUsers`,
      queryParameters,
      metadata,
    })
  }

  /* ---------------------------------- Group --------------------------------- */

  createGroup = async (
    createGroupInput: CreateGroupInput,
    metadata?: DroidMapMetadata,
  ): Promise<Group> => {
    const group = await this.callApiRouteWithToken<CreateGroupInput, Group>({
      method: 'POST',
      path: '/groups',
      body: createGroupInput,
      metadata,
    })

    if (group === undefined) {
      throw new Error(
        'Group was not created with input: ' + JSON.stringify(createGroupInput),
      )
    }

    return group
  }

  getGroup = async (
    groupId: string,
    metadata?: DroidMapMetadata,
  ): Promise<Group> => {
    const group = await this.callApiRouteWithToken<string, Group>({
      method: 'GET',
      path: `/groups/${groupId}`,
      metadata,
    })

    return group
  }

  deleteGroup = async (
    deleteGroupInput: DeleteGroupInput,
    metadata?: DroidMapMetadata,
  ): Promise<Group> => {
    const group = await this.callApiRouteWithToken<DeleteGroupInput, Group>({
      method: 'DELETE',
      path: `/groups/${deleteGroupInput.groupId}`,
      metadata,
    })

    return group
  }

  updateGroup = async (
    input: UpdateGroupInput,
    metadata?: DroidMapMetadata,
  ): Promise<Group> => {
    const group = await this.callApiRouteWithToken<UpdateGroupInput, Group>({
      method: 'PATCH',
      path: `/groups/${input.groupId}`,
      body: {
        groupId: input.groupId,
        name: input.name,
      },
      metadata,
    })

    return group
  }

  listGroupsByOrganisation = async (
    input: ListGroupsByOrganisationInput,
    metadata?: DroidMapMetadata,
  ): Promise<OffsetPaged<Group>> => {
    const { orgId, page, pageSize, filter, sortBy, sortDir } = input

    const queryParameters: QueryParameterBag =
      queryParameterBagFromOffsetPaginationControls({
        page,
        pageSize,
        filter,
        sortBy,
        sortDir,
      })

    return await this.callApiRouteWithToken<
      ListGroupsByOrganisationInput,
      OffsetPaged<Group>
    >({
      method: 'GET',
      path: `/organisations/${orgId}/groups`,
      queryParameters,
      metadata,
    })
  }

  listGroupsByOrganisationUser = async (
    input: ListGroupsByOrganisationUserInput,
    metadata?: DroidMapMetadata,
  ): Promise<OffsetPaged<Group>> => {
    const { orgId, userId, page, pageSize } = input

    const queryParameters: QueryParameterBag =
      queryParameterBagFromOffsetPaginationControls({ page, pageSize })

    return await this.callApiRouteWithToken<
      ListGroupsByOrganisationUserInput,
      OffsetPaged<Group>
    >({
      method: 'GET',
      path: `/organisations/${orgId}/organisationUsers/${userId}/groups`,
      queryParameters,
      metadata,
    })
  }

  /* ------------------------------ GroupUser ------------------------------ */

  createGroupUser = async (
    createGroupUserInput: CreateGroupUserInput,
    metadata?: DroidMapMetadata,
  ): Promise<GroupUser> => {
    const groupUser = await this.callApiRouteWithToken<
      CreateGroupUserInput,
      GroupUser
    >({
      method: 'POST',
      path: `/groups/${createGroupUserInput.groupId}/groupUsers`,
      body: createGroupUserInput,
      metadata,
    })

    if (groupUser === undefined) {
      throw new Error(
        'Group user was not created with input: ' +
          JSON.stringify(createGroupUserInput),
      )
    }

    return groupUser
  }

  deleteGroupUser = async (
    deleteGroupUserInput: DeleteGroupUserInput,
    metadata?: DroidMapMetadata,
  ): Promise<GroupUser> => {
    const groupUser = await this.callApiRouteWithToken<
      DeleteGroupUserInput,
      GroupUser
    >({
      method: 'DELETE',
      path: `/groups/${deleteGroupUserInput.groupId}/groupUsers/${deleteGroupUserInput.userId}/${deleteGroupUserInput.orgId}`,
      metadata,
    })

    return groupUser
  }

  /* ------------------------ Organisation Invitations ----------------------- */

  /**
   * Invite an email address to join an organisation (#3056). Answers with the
   * existing invitation, unchanged, when one is already open for the address,
   * and the same response whether or not the address has an account. 409 when
   * the address already belongs to a member, or when the open invitation
   * for it carries different groups (#3079); 429 at the abuse limits.
   */
  createOrganisationInvitation = async (
    input: CreateOrganisationInvitationInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrganisationInvitation> => {
    return await this.callApiRouteWithToken<
      Pick<CreateOrganisationInvitationInput, 'email' | 'groupIds'>,
      OrganisationInvitation
    >({
      method: 'POST',
      path: `/organisations/${input.orgId}/invitations`,
      body: { email: input.email, groupIds: input.groupIds },
      metadata,
    })
  }

  /**
   * Replace the groups of an open invitation (#3079). Sends no email. 404
   * when the invitation isn't open in this org, or a group isn't the org's.
   */
  updateOrganisationInvitation = async (
    input: UpdateOrganisationInvitationInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrganisationInvitation> => {
    return await this.callApiRouteWithToken<
      Pick<UpdateOrganisationInvitationInput, 'groupIds'>,
      OrganisationInvitation
    >({
      method: 'PATCH',
      path: `/organisations/${input.orgId}/invitations/${input.invitationId}`,
      body: { groupIds: input.groupIds },
      metadata,
    })
  }

  /** Open (pending, unexpired) invitations for an organisation. */
  listOrganisationInvitations = async (
    input: ListOrganisationInvitationsInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrganisationInvitationList<OrganisationInvitation>> => {
    return await this.callApiRouteWithToken<
      undefined,
      OrganisationInvitationList<OrganisationInvitation>
    >({
      method: 'GET',
      path: `/organisations/${input.orgId}/invitations`,
      metadata,
    })
  }

  /** Revoke an open invitation. */
  revokeOrganisationInvitation = async (
    input: OrganisationInvitationInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrganisationInvitation> => {
    return await this.callApiRouteWithToken<undefined, OrganisationInvitation>({
      method: 'DELETE',
      path: `/organisations/${input.orgId}/invitations/${input.invitationId}`,
      metadata,
    })
  }

  /** Re-send an open invitation's email and push its expiry out. */
  resendOrganisationInvitation = async (
    input: OrganisationInvitationInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrganisationInvitation> => {
    return await this.callApiRouteWithToken<undefined, OrganisationInvitation>({
      method: 'POST',
      path: `/organisations/${input.orgId}/invitations/${input.invitationId}/resend`,
      metadata,
    })
  }

  /**
   * The caller's own open invitations, matched on their verified email. Empty
   * — never an error — when the email is unverified.
   */
  listMyOrganisationInvitations = async (
    metadata?: DroidMapMetadata,
  ): Promise<OrganisationInvitationList<MyOrganisationInvitation>> => {
    return await this.callApiRouteWithToken<
      undefined,
      OrganisationInvitationList<MyOrganisationInvitation>
    >({
      method: 'GET',
      path: `/invitations/me`,
      metadata,
    })
  }

  /**
   * Accept an invitation addressed to the caller, making them a member and
   * adding them to the invitation's groups (#3079). 404
   * when it is not theirs or no longer open; 409 when the org is frozen or
   * pending deletion.
   */
  acceptOrganisationInvitation = async (
    input: RespondToOrganisationInvitationInput,
    metadata?: DroidMapMetadata,
  ): Promise<AcceptedOrganisationInvitation> => {
    return await this.callApiRouteWithToken<
      undefined,
      AcceptedOrganisationInvitation
    >({
      method: 'POST',
      path: `/invitations/${input.invitationId}/accept`,
      metadata,
    })
  }

  /** Decline an invitation addressed to the caller. */
  declineOrganisationInvitation = async (
    input: RespondToOrganisationInvitationInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrganisationInvitation> => {
    return await this.callApiRouteWithToken<undefined, OrganisationInvitation>({
      method: 'POST',
      path: `/invitations/${input.invitationId}/decline`,
      metadata,
    })
  }
}
